import {
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetDynamicContent,
  useGetUserDataKeys,
} from "@hooks";
import type { Document, DocumentVersion } from "@models";
import { useCallback, useEffect, useMemo } from "react";
import { toContextOptions } from "./context-options";
import { addDataKeyOwners } from "./data-key-ownership";
import { resolveDocumentReferenceOwner } from "./document-reference";
import {
  DynamicNodeProvider,
  type DynamicNodeContextValue,
} from "./dynamic-node-context";
import {
  useConditionEvaluatorBridge,
  useDataKeyRegistration,
} from "./dynamic-node-provider-hooks";
import { buildDynamicNodeResolver } from "./resolution";
import { setSlashDataKeyItems } from "./slash-command";

interface DynamicNodeEditorProviderProps {
  orgId: string;
  deviceId: string;
  /** The document being edited (provenance source for its own answers). */
  currentDocument?: Document | null;
  /** The version whose answers drive resolution (tier 1 + connected when frozen). */
  viewedVersion?: DocumentVersion | null;
  /** True when previewing an approved version — resolve against frozen answers. */
  frozen: boolean;
  /** Whether conditionals that evaluate to false should remain visible. */
  showAllConditionals?: boolean;
  /** Set whether conditionals that evaluate to false remain visible. */
  onShowAllConditionalsChange: (showAll: boolean) => void;
  /** True when the current draft was just restored from an older revision. */
  restoredWithCurrentData?: boolean;
  children: React.ReactNode;
}

/**
 * Loads the backend-resolved dynamic content (the data-key map — two-tier
 * resolution + freezing already applied server-side — plus the always-live
 * non-answer context map of device/org characteristics + document metadata),
 * builds the resolver, and provides it to the dynamic data-key / handlebars node
 * views. The stateless nodes become dumb lookups into the returned maps. The
 * `frozen` flag drives STYLING only — the backend decides freezing via
 * `readyForApproval`.
 */
export const DynamicNodeEditorProvider = ({
  orgId,
  deviceId,
  currentDocument = null,
  viewedVersion = null,
  frozen,
  showAllConditionals = false,
  onShowAllConditionalsChange,
  restoredWithCurrentData = false,
  children,
}: DynamicNodeEditorProviderProps) => {
  const { requestedKeys, registerKeys, unregisterKeys } =
    useDataKeyRegistration();

  const { data: dynamicContent, isFetching: loading } = useGetDynamicContent(
    orgId,
    currentDocument?.id ?? "",
    viewedVersion?.id,
    requestedKeys,
    Boolean(orgId && currentDocument?.id)
  );
  const { data: userDataKeys } = useGetUserDataKeys();
  const ownerQueriesEnabled = Boolean(orgId && deviceId);
  const { data: documents = [] } = useGetDocuments(orgId, deviceId, {
    includeOrgDocs: true,
    enabled: ownerQueriesEnabled,
  });
  const { data: templatesConfig = {} } = useGetDocumentsTemplates({
    orgId,
    deviceId,
    enabled: ownerQueriesEnabled,
  });
  const dataKeyOptions = useMemo(
    () =>
      addDataKeyOwners({
        items: (userDataKeys ?? []).map((item) => ({
          key: item.key,
          label: item.label,
        })),
        documents,
        templatesConfig,
        currentDocument,
        viewedVersion,
      }),
    [currentDocument, documents, templatesConfig, userDataKeys, viewedVersion]
  );

  const isKeyPending = useCallback(
    (key: string): boolean =>
      loading && !(key in (dynamicContent?.dataKeys ?? {})),
    [loading, dynamicContent]
  );

  // Publish the user's data keys to the slash-command registry (the Suggestion
  // plugin reads them outside React when the `/` menu opens).
  useEffect(() => {
    setSlashDataKeyItems(dataKeyOptions);
  }, [dataKeyOptions]);

  const value = useMemo<DynamicNodeContextValue>(() => {
    const context = dynamicContent?.context ?? {};
    const resolver = buildDynamicNodeResolver({
      dataKeys: dynamicContent?.dataKeys ?? {},
      context,
    });

    return {
      resolveDataKey: resolver.resolveDataKey,
      resolveContext: resolver.resolveContext,
      contextKeys: toContextOptions(context, documents, currentDocument?.id),
      resolveDocumentReferenceOwner: (key, did) =>
        resolveDocumentReferenceOwner(key, did, documents),
      evaluateHandlebars: resolver.evaluateHandlebars,
      showAllConditionals,
      onShowAllConditionalsChange,
      frozen,
      restoredWithCurrentData,
      loading,
      registerKeys,
      unregisterKeys,
      isKeyPending,
    };
  }, [
    dynamicContent,
    documents,
    currentDocument?.id,
    frozen,
    showAllConditionals,
    onShowAllConditionalsChange,
    restoredWithCurrentData,
    loading,
    registerKeys,
    unregisterKeys,
    isKeyPending,
  ]);

  useConditionEvaluatorBridge(value);

  return <DynamicNodeProvider value={value}>{children}</DynamicNodeProvider>;
};

export default DynamicNodeEditorProvider;
